import React, { useCallback } from 'react';
import { useThrottledCallback } from './useThrottledCallback.js';
import { isDefined } from '@fabric-app/utils/guards';
import { noop } from '@fabric-app/utils/guards/noop';

const SCROLL_CONTINUOUS_DURATION = 250;
const useScrollPosition = (ref, options = {
  onScroll: noop
}) => {
  const [scrollPosition, setScrollPosition] = React.useState(null);
  const prevScrollPositionRef = React.useRef(null);
  const onScrollRef = React.useRef(options.onScroll);
  onScrollRef.current = options.onScroll;
  const lastScrollEventRef = React.useRef(null);
  const resetLastScrollEvent = useCallback(() => {
    lastScrollEventRef.current = null;
  }, []);
  const updateScrollPositionRef = useThrottledCallback((byUser) => {
    const refEl = ref instanceof HTMLElement ? ref : ref?.current;
    if (!refEl) return;
    const atTop = refEl.scrollTop <= 0;
    const atBottom = refEl.scrollHeight - refEl.clientHeight <= refEl.scrollTop;
    const atLeft = refEl.scrollLeft <= 0;
    const atRight = refEl.scrollWidth - refEl.clientWidth <= refEl.scrollLeft;
    const hasVerticalScroll = refEl.scrollHeight > refEl.clientHeight;
    const hasHorizontalScroll = refEl.scrollWidth > refEl.clientWidth;
    const currentScrollPosition = {
      x: refEl.scrollLeft,
      y: refEl.scrollTop,
      vertical: hasVerticalScroll ? !atTop && !atBottom ? "middle" : atTop ? "top" : "bottom" : "n/a",
      horizontal: hasHorizontalScroll ? !atLeft && !atRight ? "center" : atLeft ? "left" : "right" : "n/a",
      totalX: refEl.scrollWidth - refEl.clientWidth,
      totalY: refEl.scrollHeight - refEl.clientHeight
    };
    setScrollPosition(currentScrollPosition);
    if (byUser && prevScrollPositionRef.current && onScrollRef.current) {
      const now = Date.now();
      const lastScrollEvent = lastScrollEventRef.current && now - lastScrollEventRef.current.timestamp < SCROLL_CONTINUOUS_DURATION ? lastScrollEventRef.current : null;
      const prevScrollPosition = prevScrollPositionRef.current;
      const totalDistanceX = currentScrollPosition.x - prevScrollPosition?.x + (lastScrollEvent?.direction.x.distance ?? 0);
      const totalDistanceY = currentScrollPosition.y - prevScrollPosition?.y + (lastScrollEvent?.direction.y.distance ?? 0);
      const directionY = totalDistanceY > 0 ? "down" : totalDistanceY < 0 ? "up" : "n/a";
      const directionX = totalDistanceX > 0 ? "right" : totalDistanceX < 0 ? "left" : "n/a";
      const distanceY = totalDistanceY;
      const distanceX = totalDistanceX;
      const scrollEventData = {
        scrollPosition: currentScrollPosition,
        direction: {
          y: {
            direction: directionY,
            distance: distanceY
          },
          x: {
            direction: directionX,
            distance: distanceX
          }
        },
        duration: lastScrollEvent ? now - lastScrollEvent.timestamp + lastScrollEvent.duration : 0,
        reset: resetLastScrollEvent
      };
      onScrollRef.current?.(scrollEventData);
      lastScrollEventRef.current = {
        ...scrollEventData,
        timestamp: now
      };
    }
    prevScrollPositionRef.current = currentScrollPosition;
  }, 50);
  React.useEffect(() => {
    const updateScrollPosition = updateScrollPositionRef.current;
    const refEl = ref instanceof HTMLElement ? ref : ref?.current;
    if (refEl) {
      updateScrollPosition(false);
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (isDefined(entry) && isDefined(refEl)) {
          updateScrollPosition(false);
        }
      });
      resizeObserver.observe(refEl);
      resizeObserver.observe(document.body);
      if (options?.observeParentResize && refEl.parentElement) {
        resizeObserver.observe(refEl.parentElement);
      }
      const handleScrollByUser = () => updateScrollPosition(true);
      window.addEventListener("scroll", handleScrollByUser);
      refEl.addEventListener("scroll", handleScrollByUser);
      return () => {
        setScrollPosition(null);
        resizeObserver.disconnect();
        window.removeEventListener("scroll", handleScrollByUser);
        refEl.removeEventListener("scroll", handleScrollByUser);
      };
    } else {
      setScrollPosition(null);
    }
    return () => {
      if ("cancel" in updateScrollPosition) updateScrollPosition.cancel();
    };
  }, [
    ref,
    options?.observeParentResize,
    updateScrollPositionRef
  ]);
  return [
    scrollPosition,
    setScrollPosition
  ];
};

export { useScrollPosition };
//# sourceMappingURL=useScrollPosition.js.map
